import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { CareOrder } from '@talos/kyoko';
import {
  abbreviateId,
  ACTION,
  DecisionStatusEnum,
  IconName,
  isTalosUser,
  MixpanelEvent,
  NotificationVariants,
  OrdStatusEnum,
  request,
  ROLE,
  useCallbackRef,
  useDisclosureWithContext,
  useEndpointsContext,
  useGlobalDialog,
  useGlobalToasts,
  useMixpanel,
  useUserContext,
} from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { useMemo, type ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import { modify } from '../../../../components/OMS/CareOrderFormView/CareOrderSlice';
import { closeView, openView } from '../../../../components/OMS/OMSSlice';
import { OMSView } from '../../../../components/OMS/OMSView';
import { useFeatureFlag } from '../../../../hooks';
import { useAppStateDispatch, useAppStateStore } from '../../../../providers/AppStateProvider';
import { DatadogTradingAction, trackCareOrderAction } from '../../../../utils/datadogTradingActions';
import { useApproveCareOrderComplianceMutation } from '../careOrderComplianceApi';
import {
  CareOrderNettingPreviewDialog,
  CareOrderNettingPreviewMode,
  type CareOrderNettingPreviewDialogContext,
} from '../CareOrderNettingPreview/CareOrderNettingPreviewDialog';

interface GetCancelMenuItemParams {
  entity: CareOrder;
  isCancelable: boolean;
}

export function useCareOrderActionMenuItems() {
  const mixpanel = useMixpanel();
  const { user } = useUserContext();
  const { isAuthorized, isRole } = useRoleAuth();
  const dispatch = useAppStateDispatch();
  const store = useAppStateStore();
  const { open } = useGlobalDialog();
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useEndpointsContext();
  const { enableCareOrderWriting: enableCareOrderWritingFlag, workingRequireOrderActivation } = useFeatureFlag();
  const [approveCareOrderCompliance] = useApproveCareOrderComplianceMutation();
  const nettingPreviewDisclosure = useDisclosureWithContext<CareOrderNettingPreviewDialogContext>();

  const enableCareOrderWriting = enableCareOrderWritingFlag || isTalosUser(user);

  const handleForceCancel = useCallbackRef((entity: CareOrder) => {
    const orderTypeLabel = entity.isNetted ? 'Netted Order' : 'Care Order';

    open({
      title: `Force cancel ${orderTypeLabel}`,
      content: (
        <>{`Are you sure you want to force cancel ${orderTypeLabel.toLowerCase()} #${abbreviateId(entity.OrderID)}?`}</>
      ),
      onConfirm: () => {
        mixpanel.track(MixpanelEvent.ForceCancelCareOrder);
        request(
          'PUT',
          entity.isNetted
            ? `${orgApiEndpoint}/care/working/orders/${entity.OrderID}/force-cancel`
            : `${orgApiEndpoint}/care/orders/${entity.OrderID}/force-cancel`
        )
          .then(() => {
            addToast({
              text: `${orderTypeLabel} force cancelled.`,
              variant: NotificationVariants.Positive,
            });
          })
          .catch((e: Error) =>
            addToast({
              text: `Could not force cancel ${orderTypeLabel}: ${e.message}`,
              variant: NotificationVariants.Negative,
            })
          );
      },
    });
  });

  const handleCancel = useCallbackRef((entity: CareOrder) => {
    const orderTypeLabel = entity.isNetted ? 'Netted Order' : 'Care Order';

    open({
      title: `Cancel ${orderTypeLabel}`,
      content: (
        <>
          <p>{`Are you sure you want to cancel ${orderTypeLabel} #${abbreviateId(entity.OrderID)}?`}</p>
        </>
      ),
      onConfirm: () => {
        const clOrdID = uuid();
        trackCareOrderAction(DatadogTradingAction.Cancel, { clOrdID, orderID: entity.OrderID });
        request(
          'PUT',
          entity.isNetted ? `${orgApiEndpoint}/care/working/orders/cancel` : `${orgApiEndpoint}/care/orders/cancel`,
          {
            ClOrdId: clOrdID,
            OrderID: entity.OrderID,
          }
        )
          .then(() => {
            const state = store.getState();
            if (
              state.careOrder.careOrderBeingModified?.OrderID === entity.OrderID &&
              state.OMS.openedView === OMSView.CareOrderForm
            ) {
              dispatch(closeView());
            }

            addToast({
              text: `${orderTypeLabel} cancelled.`,
              variant: NotificationVariants.Positive,
            });
          })
          .catch((e: Error) =>
            addToast({
              text: `Could not cancel ${orderTypeLabel}: ${e.message}`,
              variant: NotificationVariants.Negative,
            })
          );
      },
    });
  });

  const handleModifyOrder = useCallbackRef((entity: CareOrder) => {
    dispatch(modify(entity));
    dispatch(openView(OMSView.CareOrderForm));
  });

  const handleApproveCompliance = useCallbackRef((entity: CareOrder) => {
    void approveCareOrderCompliance({ orderID: entity.OrderID });
  });

  const openReadOnlyNettingPreview = useCallbackRef(
    (
      entity: CareOrder,
      mode: typeof CareOrderNettingPreviewMode.Approval | typeof CareOrderNettingPreviewMode.View
    ) => {
      // The dialog derives its member rows from the persisted WorkingOrder; the row itself seeds the preview header.
      nettingPreviewDisclosure.open({
        mode,
        selectedCareOrders: [entity],
        nettedCareOrder: entity,
      });
    }
  );

  const handleApproveWorkingOrder = useCallbackRef((entity: CareOrder) => {
    openReadOnlyNettingPreview(entity, CareOrderNettingPreviewMode.Approval);
  });

  const handleViewNetting = useCallbackRef((entity: CareOrder) => {
    openReadOnlyNettingPreview(entity, CareOrderNettingPreviewMode.View);
  });

  const getModifyMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    if (!isAuthorized(ACTION.SUBMIT_CARE_ORDER) || !enableCareOrderWriting || entity.isNetted) {
      return undefined;
    }

    return {
      name: 'Modify',
      disabled: !entity.isModifiable,
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      action: () => handleModifyOrder(entity),
    };
  });

  const getApproveComplianceMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    // Do not use ACTION auth here: talos.admin bypasses action checks, but this endpoint is PCA-only.
    if (!isRole(ROLE.PORTFOLIO_COMPLIANCE_ADMIN) || entity.OrdStatus !== OrdStatusEnum.PendingNew) {
      return undefined;
    }

    return {
      name: 'Approve compliance',
      icon: `<i class="ag-icon ${IconName.Check}"/>`,
      action: () => handleApproveCompliance(entity),
    };
  });

  const getApproveWorkingOrderMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    // This approval is only for netted WorkingOrders held before activation by working.RequireOrderActivation.
    if (
      !workingRequireOrderActivation ||
      !isRole(ROLE.TRADER) ||
      !entity.isNetted ||
      entity.OrdStatus !== OrdStatusEnum.New ||
      entity.DecisionStatus !== DecisionStatusEnum.PendingActivation
    ) {
      return undefined;
    }

    const createdByCurrentUser = entity.User === user.Name;

    return {
      name: 'Approve',
      // Backend requires a different trader to activate the WorkingOrder; keep the item visible to explain why.
      disabled: createdByCurrentUser,
      tooltip: createdByCurrentUser ? 'The user who created the netted care order cannot approve it' : undefined,
      icon: `<i class="ag-icon ${IconName.Check}"/>`,
      action: () => handleApproveWorkingOrder(entity),
    };
  });

  const getViewNettingMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    if (!entity.isNetted) {
      return undefined;
    }

    return {
      name: 'View Netting',
      icon: `<i class="ag-icon ${IconName.EyeShow}"/>`,
      action: () => handleViewNetting(entity),
    };
  });

  const getCancelMenuItem = useCallbackRef(
    ({ entity, isCancelable }: GetCancelMenuItemParams): MenuItemDef | undefined => {
      if (!isAuthorized(ACTION.CANCEL_ORDER) || !enableCareOrderWriting) {
        return undefined;
      }

      return {
        name: 'Cancel',
        disabled: !isCancelable,
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
        action: () => handleCancel(entity),
      };
    }
  );

  const getForceCancelMenuItem = useCallbackRef((entity: CareOrder): MenuItemDef | undefined => {
    if (!isAuthorized(ACTION.FORCE_CANCEL_CARE_ORDER) || !enableCareOrderWriting) {
      return undefined;
    }

    return {
      name: 'Force cancel',
      action: () => handleForceCancel(entity),
    };
  });

  const dialogs: ReactNode = useMemo(
    () => <CareOrderNettingPreviewDialog disclosure={nettingPreviewDisclosure} />,
    [nettingPreviewDisclosure]
  );

  return {
    getModifyMenuItem,
    getApproveComplianceMenuItem,
    getApproveWorkingOrderMenuItem,
    getViewNettingMenuItem,
    getCancelMenuItem,
    getForceCancelMenuItem,
    dialogs,
  };
}
